import {useQuery} from '@tanstack/react-query';
import {useScope} from '../contexts/ScopeContext';
import apiClient from '../services/api';

export type SourceCoverage = {status:string;reason?:string;missing_sources?:string[]};
export type PortalSource = {portal_id:string;environment_id:string;observed_at:string;coverage?:SourceCoverage;cursor?:string|null};
export type Association = {association_id:string;source_id:string;source_type:string;resource_id:string;pipeline_id?:string|null;version:string|number;provenance:{source_type:'versioned_registry_membership';registry_version:string|number;registry_sha256:string;relation:'resources.applications';association_identity:'derived_from_exact_membership'};observed_at:string};
export type Sources = PortalSource & {associations:Association[]};
export type GeneralFields = {display_name:string;feature_profile_id:string|null};
export type General = PortalSource & {draft_id?:string;created_at?:string;actor?:string;version:string|number;fields:GeneralFields;feature_profiles:{id:string;label:string}[];can_configure:boolean};
export type PortalEvent = {id:string;type:string;source_type:string;source_id:string;occurred_at:string|null;observed_at:string;actor:string|null;result:string|null};
export type Timeline = PortalSource & {events:PortalEvent[]};
export function unwrapPortal<T>(input:unknown):T {
 const value=input as Record<string,unknown>;
 if(!value||typeof value!=='object')throw Error('Portal source unavailable');
 if(value.schemaVersion){
  if(!['known','empty','stale'].includes(String(value.status)))throw Error('Portal source unavailable');
  if(!value.data||typeof value.data!=='object')throw Error('Portal source unavailable');
  return {...value.data as object,observed_at:(value.data as Record<string,unknown>).observed_at??value.observedAt} as T;
 }
 return value as T;
}
export const sourceText=(value:unknown):value is string=>typeof value==='string'&&value.length>0&&value.length<=2048&&!/[\x00-\x08\x0b\x0c\x0e-\x1f]/.test(value);
export function sourceIdentity<T extends PortalSource>(value:T,portal:string,environment:string):T {
 if(value.portal_id!==portal||value.environment_id!==environment||!sourceText(value.observed_at))throw Error('Portal scope mismatch');
 return value;
}
export function usePortalSource<T extends PortalSource>(portalId:string,resource:string,parse:(value:T)=>T,params:Record<string,string>={}){
 const scope=useScope();
 const environment=scope.resolved.kind==='all'?'all':scope.environmentId??'';
 const enabled=!!portalId&&!!environment&&scope.resolved.kind==='environment';
 const query=useQuery({queryKey:scope.queryKey('portal-source',portalId,resource,environment,params),enabled,retry:false,
  queryFn:async({signal})=>parse(sourceIdentity(unwrapPortal<T>((await apiClient.get('/portals/'+encodeURIComponent(portalId)+'/'+resource,{signal,params:{environmentId:environment,...params}})).data),portalId,environment))});
 return {query,scope,environment,enabled};
}
export function portalEvidenceLink(type:string,id:string,portal:string,environment:string):string|null {
 if(!sourceText(id))return null;
 const params=new URLSearchParams({environmentId:environment});
 if(environment==='all'){params.delete('environmentId');params.set('environmentScope','all');}
 switch(type){
  case 'release':return '/releases/'+encodeURIComponent(id)+'/history?'+params;
  case 'recovery_operation':params.set('recovery_view','monitor');params.set('recovery_operation',id);return '/backups?'+params;
  case 'portal_configuration_draft':
  case 'configuration_draft':params.set('draft_id',id);return '/portals/'+encodeURIComponent(portal)+'/configuration/general?'+params;
  case 'data_source':params.set('sourceId',id);return '/operations/data-flows/sources?'+params;
  case 'ingestion_job':return '/ingestion/jobs/'+encodeURIComponent(id)+'?'+params;
  default:return null;
 }
}
